<template>
  <div class="onboarding-portal">
    <div class="onboarding-card">
      <div v-if="loading" class="onboarding-loading">
        <div class="hr-spinner"></div>
        <p>Loading your onboarding…</p>
      </div>

      <div v-else-if="error" class="onboarding-error">
        <h2>Unable to open onboarding</h2>
        <p>{{ error }}</p>
      </div>

      <div v-else-if="submitted" class="onboarding-success">
        <h2>Thank you</h2>
        <p>Your documents have been submitted. HR will review and contact you if anything else is needed.</p>
      </div>

      <template v-else-if="invite">
        <header class="onboarding-header">
          <h1>Welcome, {{ invite.applicant_name }}</h1>
          <p class="onboarding-sub">
            Complete the forms below for {{ companyLabel }}. Fields marked with your profile information are pre-filled — please verify and update if needed.
          </p>
        </header>

        <div class="onboarding-form-block hr-bg-light" style="padding: 1.5rem; margin-bottom: 1.5rem; border-radius: 8px;">
          <h3>Personal Details</h3>
          <p class="hr-text-muted" style="margin-bottom: 1rem;">Please provide your complete personal information.</p>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
            <div><label class="hr-form-label">First Name <span class="req">*</span></label><input v-model="profile.firstName" type="text" class="hr-input" /></div>
            <div><label class="hr-form-label">Middle Name</label><input v-model="profile.middleName" type="text" class="hr-input" /></div>
            <div><label class="hr-form-label">Last Name <span class="req">*</span></label><input v-model="profile.lastName" type="text" class="hr-input" /></div>
            <div><label class="hr-form-label">Phone Number <span class="req">*</span></label><input v-model="profile.phone" type="tel" class="hr-input" /></div>
            <div><label class="hr-form-label">Personal Email <span class="req">*</span></label><input v-model="profile.personalEmail" type="email" class="hr-input" /></div>
            <div><label class="hr-form-label">National ID / Passport <span class="req">*</span></label><input v-model="profile.nationalId" type="text" class="hr-input" /></div>
            <div><label class="hr-form-label">Date of Birth <span class="req">*</span></label><input v-model="profile.dateOfBirth" type="date" class="hr-input" /></div>
            <div><label class="hr-form-label">Gender <span class="req">*</span></label>
              <select v-model="profile.gender" class="hr-input">
                <option value="">Select...</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div><label class="hr-form-label">Marital Status</label>
              <select v-model="profile.maritalStatus" class="hr-input">
                <option value="">Select...</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            </div>
            <div><label class="hr-form-label">Blood Group</label>
              <select v-model="profile.bloodGroup" class="hr-input">
                <option value="">Select...</option>
                <option value="A+">A+</option><option value="A-">A-</option><option value="B+">B+</option><option value="B-">B-</option>
                <option value="AB+">AB+</option><option value="AB-">AB-</option><option value="O+">O+</option><option value="O-">O-</option>
              </select>
            </div>
            <div style="grid-column: 1 / -1;"><label class="hr-form-label">Physical Address</label><textarea v-model="profile.address" class="hr-textarea" rows="2"></textarea></div>
          </div>
        </div>

        <div class="onboarding-form-block hr-bg-light" style="padding: 1.5rem; margin-bottom: 1.5rem; border-radius: 8px;">
          <h3>Statutory & Bank Details</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
            <div><label class="hr-form-label">KRA PIN</label><input v-model="profile.kraPin" type="text" class="hr-input" /></div>
            <div><label class="hr-form-label">NSSF Number</label><input v-model="profile.nssf" type="text" class="hr-input" /></div>
            <div><label class="hr-form-label">NHIF Number</label><input v-model="profile.nhif" type="text" class="hr-input" /></div>
            <div></div>
            <div><label class="hr-form-label">Bank Name</label><input v-model="profile.bankName" type="text" class="hr-input" /></div>
            <div><label class="hr-form-label">Branch Name</label><input v-model="profile.branchName" type="text" class="hr-input" /></div>
            <div><label class="hr-form-label">Account Number</label><input v-model="profile.accountNumber" type="text" class="hr-input" /></div>
          </div>
        </div>

        <div class="onboarding-form-block hr-bg-light" style="padding: 1.5rem; margin-bottom: 1.5rem; border-radius: 8px;">
          <h3>Emergency Contact & Next of Kin</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
            <div style="grid-column: 1 / -1;"><strong style="font-size: 0.9rem;">Emergency Contact</strong></div>
            <div><label class="hr-form-label">Name</label><input v-model="profile.emergencyName" type="text" class="hr-input" /></div>
            <div><label class="hr-form-label">Relation</label><input v-model="profile.emergencyRelation" type="text" class="hr-input" /></div>
            <div><label class="hr-form-label">Phone</label><input v-model="profile.emergencyPhone" type="tel" class="hr-input" /></div>
            <div></div>
            <div style="grid-column: 1 / -1; margin-top: 1rem;"><strong style="font-size: 0.9rem;">Next of Kin</strong></div>
            <div><label class="hr-form-label">Name</label><input v-model="profile.nokName" type="text" class="hr-input" /></div>
            <div><label class="hr-form-label">Relation</label><input v-model="profile.nokRelation" type="text" class="hr-input" /></div>
            <div><label class="hr-form-label">Phone</label><input v-model="profile.nokPhone" type="tel" class="hr-input" /></div>
            <div><label class="hr-form-label">National ID</label><input v-model="profile.nokId" type="text" class="hr-input" /></div>
          </div>
        </div>

        <div class="onboarding-form-block hr-bg-light" style="padding: 1.5rem; margin-bottom: 1.5rem; border-radius: 8px;">
          <h3>Documents</h3>
          <p class="hr-text-muted" style="margin-bottom: 1rem;">Upload all necessary registration documents (ID, Certificates, Resume).</p>
          <div v-for="(doc, index) in documents" :key="index" style="display: grid; grid-template-columns: 1fr 1fr auto; gap: 1rem; align-items: end; margin-bottom: 1rem;">
            <div>
              <label class="hr-form-label">Document Name</label>
              <input v-model="doc.documentName" type="text" class="hr-input" placeholder="e.g. ID Copy" />
            </div>
            <div>
              <label class="hr-form-label">File</label>
              <input type="file" class="hr-input" @change="e => handleFileUpload(e, index)" />
            </div>
            <button v-if="documents.length > 1" type="button" @click="removeDocument(index)" class="hr-btn hr-btn-danger" style="padding: 0.5rem;"><i class="bx bx-trash"></i></button>
          </div>
          <button type="button" @click="addDocument" class="hr-btn hr-btn-outline-primary hr-btn-sm" style="margin-top: 0.5rem;"><i class="bx bx-plus"></i> Add Document</button>
        </div>

        <div v-for="form in forms" :key="form.id" class="onboarding-form-block">
          <h3>{{ form.template_name }}</h3>
          <p v-if="form.description" class="hr-text-muted">{{ form.description }}</p>

          <div v-for="field in form.fields" :key="field.id" class="onboarding-field">
            <label class="hr-form-label">
              {{ field.field_label }}
              <span v-if="field.is_required == 1" class="req">*</span>
            </label>
            <textarea
              v-if="field.field_type === 'textarea'"
              v-model="responses[form.id][field.field_key]"
              class="hr-textarea"
              rows="3"
            />
            <input
              v-else
              v-model="responses[form.id][field.field_key]"
              class="hr-input"
              :type="field.field_type === 'email' ? 'email' : field.field_type === 'date' ? 'date' : 'text'"
            />
          </div>
        </div>

        <div class="onboarding-actions">
          <button type="button" class="hr-btn hr-btn-primary" :disabled="submitting" @click="submitAll">
            {{ submitting ? "Submitting…" : "Submit all forms" }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute, toast } from "@/api";

export default {
  name: "OnboardingPortal",
  data() {
    return {
      loading: true,
      error: "",
      submitted: false,
      submitting: false,
      invite: null,
      profile: {
        firstName: "", middleName: "", lastName: "", personalEmail: "", phone: "",
        nationalId: "", dateOfBirth: "", gender: "", maritalStatus: "", bloodGroup: "", address: "",
        kraPin: "", nssf: "", nhif: "", bankName: "", branchName: "", accountNumber: "",
        emergencyName: "", emergencyRelation: "", emergencyPhone: "",
        nokName: "", nokRelation: "", nokPhone: "", nokId: ""
      },
      documents: [{ documentName: "National ID Copy", file: null }, { documentName: "Resume / CV", file: null }],
      forms: [],
      responses: {}
    };
  },
  computed: {
    companyLabel() {
      return this.invite?.company_name || "your employer";
    },
    token() {
      return this.$route.params.token || "";
    }
  },
  mounted() {
    this.loadInvite();
  },
  methods: {
    loadInvite() {
      if (!this.token) {
        this.error = "Invalid link.";
        this.loading = false;
        return;
      }
      const data = new FormData();
      data.append("function", "getRecruitmentOnboardingByToken");
      data.append("token", this.token);
      execute(data, authUrl)
        .then((res) => {
          this.loading = false;
          if (!res.data.success || !res.data.data) {
            this.error = res.data.message || "This link is invalid or has expired.";
            return;
          }
          const payload = res.data.data;
          if (payload.expired) {
            this.error = "This onboarding link has expired. Please contact HR for a new invitation.";
            return;
          }
          this.invite = payload;
          const parts = (payload.applicant_name || "").split(/\s+/);
          this.profile.firstName = parts[0] || "";
          this.profile.lastName = parts.slice(1).join(" ") || "";
          this.profile.personalEmail = payload.email || "";
          this.profile.phone = payload.phone || "";
          this.profile.nationalId = payload.national_id || "";
          this.forms = Array.isArray(payload.forms) ? payload.forms : [];
          const map = {};
          this.forms.forEach((f) => {
            const id = f.id;
            map[id] = { ...(f.responses || {}) };
          });
          this.responses = map;
        })
        .catch(() => {
          this.loading = false;
          this.error = "Could not load onboarding. Please try again later.";
        });
    },
    addDocument() {
      this.documents.push({ documentName: "", file: null });
    },
    removeDocument(index) {
      this.documents.splice(index, 1);
    },
    handleFileUpload(event, index) {
      const file = event.target.files[0];
      if (file) {
        this.documents[index].file = file;
      }
    },
    submitAll() {
      const payload = this.forms.map((f) => ({
        submissionId: f.id,
        responses: this.responses[f.id] || {}
      }));
      for (const f of this.forms) {
        for (const field of f.fields || []) {
          if (field.is_required == 1) {
            const val = (this.responses[f.id] || {})[field.field_key];
            if (val === undefined || val === null || String(val).trim() === "") {
              toast.warning(`Please complete: ${field.field_label} (${f.template_name})`);
              return;
            }
          }
        }
      }
      if (!this.profile.firstName || !this.profile.lastName || !this.profile.personalEmail || !this.profile.phone || !this.profile.nationalId || !this.profile.dateOfBirth || !this.profile.gender) {
        toast.warning("Please fill all required personal details (*).");
        return;
      }

      this.submitting = true;
      const profileData = new FormData();
      profileData.append("function", "submitRecruitFullProfile");
      profileData.append("token", this.token);
      profileData.append("profile", JSON.stringify(this.profile));
      
      this.documents.forEach((doc, i) => {
        if (doc.file) {
          profileData.append(`document_file_${i}`, doc.file);
          profileData.append(`document_name_${i}`, doc.documentName || `Document ${i+1}`);
        }
      });
      profileData.append("documentCount", this.documents.length);

      execute(profileData, authUrl)
        .then((res) => {
          if (!res.data.success) {
            toast.error(res.data.message || "Failed to update profile.");
            this.submitting = false;
            return;
          }
          
          const data = new FormData();
          data.append("function", "submitRecruitmentOnboarding");
          data.append("token", this.token);
          data.append("responses", JSON.stringify(payload));
          execute(data, authUrl)
            .then((res) => {
              this.submitting = false;
              if (res.data.success) {
                this.submitted = true;
              } else {
                toast.error(res.data.message || "Failed to submit onboarding.");
              }
            })
            .catch(() => {
              this.submitting = false;
              toast.error("An error occurred during submission.");
            });
        })
        .catch(() => {
          this.submitting = false;
          toast.error("Failed to upload profile and documents.");
        });
    }
  }
};
</script>

<style scoped>
.onboarding-portal {
  min-height: 100vh;
  background: linear-gradient(160deg, #f0f4ff 0%, #f8fafc 50%, #eef2ff 100%);
  padding: 32px 16px;
  box-sizing: border-box;
}
.onboarding-card {
  max-width: 720px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.08);
  padding: 28px 32px;
}
.onboarding-header h1 {
  margin: 0 0 8px;
  font-size: 1.5rem;
  color: #1e293b;
}
.onboarding-sub {
  color: #64748b;
  margin: 0 0 24px;
  line-height: 1.5;
}
.onboarding-form-block {
  border-top: 1px solid #e2e8f0;
  padding-top: 20px;
  margin-top: 20px;
}
.onboarding-form-block h3 {
  margin: 0 0 8px;
  font-size: 1.1rem;
}
.onboarding-field {
  margin-bottom: 16px;
}
.req {
  color: #dc2626;
}
.onboarding-actions {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
}
.onboarding-loading,
.onboarding-error,
.onboarding-success {
  text-align: center;
  padding: 40px 16px;
}
.onboarding-error h2,
.onboarding-success h2 {
  color: #1e293b;
}
</style>
